import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Building2, Users, TrendingUp, AlertCircle, Plus, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function FirmPortal() {
  const navigate = useNavigate();
  const [selectedInstitution, setSelectedInstitution] = useState<string | null>(null);

  const { data: firmData, isLoading: firmLoading } = useQuery({
    queryKey: ["firm-data"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Get user's firm membership
      const { data: membership, error: membershipError } = await supabase
        .from("firm_members" as any)
        .select("*, consulting_firms(*)")
        .eq("user_id", user.id)
        .single();

      if (membershipError) throw membershipError;

      // Get all institution access for this firm
      const { data: access, error: accessError } = await supabase
        .from("firm_institution_access" as any)
        .select("*, institution_settings(*)")
        .eq("firm_id", (membership as any).firm_id)
        .is("revoked_at", null);

      if (accessError) throw accessError;

      return {
        firm: (membership as any).consulting_firms,
        role: (membership as any).role,
        institutions: access || [],
      };
    },
  });

  const { data: aggregateStats, isLoading: statsLoading } = useQuery({
    queryKey: ["firm-aggregate-stats", firmData?.institutions],
    enabled: !!firmData?.institutions,
    queryFn: async () => {
      if (!firmData?.institutions || firmData.institutions.length === 0) {
        return {
          totalInstitutions: 0,
          totalDeals: 0,
          totalAthletes: 0,
          avgComplianceRate: 0,
        };
      }

      // Get deals count across all institutions
      const { count: dealsCount } = await supabase
        .from("deals" as any)
        .select("*", { count: "exact", head: true });

      // Get athletes count
      const { count: athletesCount } = await supabase
        .from("athletes" as any)
        .select("*", { count: "exact", head: true });

      // Calculate average compliance rate
      const { data: deals } = await supabase
        .from("deals" as any)
        .select("status");

      const approvedDeals = deals?.filter((d: any) => d.status === "active").length || 0;
      const totalDeals = deals?.length || 1;
      const complianceRate = (approvedDeals / totalDeals) * 100;

      return {
        totalInstitutions: firmData.institutions.length,
        totalDeals: dealsCount || 0,
        totalAthletes: athletesCount || 0,
        avgComplianceRate: complianceRate,
      };
    },
  });

  if (firmLoading || statsLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (!firmData) {
    return (
      <DashboardLayout>
        <Card>
          <CardContent className="py-12">
            <div className="text-center space-y-4">
              <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground" />
              <h3 className="font-semibold text-lg">No Firm Access</h3>
              <p className="text-muted-foreground">
                You don't have access to any consulting firm. Contact your administrator.
              </p>
            </div>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">{firmData.firm.name}</h1>
            <p className="text-muted-foreground mt-1">
              Managing {firmData.institutions.length} institution{firmData.institutions.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => navigate("/firm/cross-institution-dashboard")}
              variant="outline"
            >
              <TrendingUp className="mr-2 h-4 w-4" />
              Analytics Dashboard
            </Button>
            {firmData.role === "firm_admin" && (
              <Button onClick={() => navigate("/firm/settings")}>
                <Building2 className="mr-2 h-4 w-4" />
                Firm Settings
              </Button>
            )}
          </div>
        </div>

        {/* Aggregate Stats */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Institutions</CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{aggregateStats?.totalInstitutions || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Deals</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{aggregateStats?.totalDeals || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Athletes</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{aggregateStats?.totalAthletes || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Avg Compliance Rate</CardTitle>
              <AlertCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {aggregateStats?.avgComplianceRate.toFixed(1)}%
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Client Institutions List */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Client Institutions</CardTitle>
                <CardDescription>
                  Access and manage your institutional clients
                </CardDescription>
              </div>
              {firmData.role === "firm_admin" && (
                <Button size="sm" variant="outline">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Institution
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {firmData.institutions.map((access: any) => (
                <Card key={access.id} className="hover:border-primary/50 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">
                          {access.institution_settings.institution_name || "Unnamed Institution"}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          Access Level: <Badge variant="outline">{access.access_level}</Badge>
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Granted: {new Date(access.granted_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedInstitution(access.institution_id);
                            navigate("/dashboard");
                          }}
                        >
                          <ExternalLink className="mr-2 h-4 w-4" />
                          View Dashboard
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {firmData.institutions.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No institutions assigned yet</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
